import {
  Bird,
  Droplets,
  Flower,
  HeartPulse,
  ListTree,
  FlaskConical,
  GitFork,
  Grid3x3,
  Dna,
  Microscope,
  Leaf,
  Scissors,
  ShieldPlus,
  Sprout,
  Sun,
  Split,
  Trees,
} from 'lucide-react';

import { biologyModuleUrl } from '../../../lib/biologyModule';

import type { StudyTool } from './studyToolTypes';

const CELL_EXPLORER_URL = biologyModuleUrl();

/**
 * Biology tools mount directly from the same-origin CDN module. The same module path is used locally and in production.
 */
export const BIOLOGY_TOOLS: StudyTool[] = CELL_EXPLORER_URL
  ? [
      {
        id: 'cell-explorer',
        subjectKey: 'biology',
        category: 'cells',
        name: 'Cell Explorer',
        description:
          'Turn and cut open 3D animal and plant cells. Tour every part, compare the two cells and test yourself.',
        path: '/learn/study/subjects/biology/tools/cell-explorer',
        actionLabel: 'Open Cell Explorer',
        icon: Microscope,
        tone: 'mint',
      },
      {
        id: 'membrane-transport',
        subjectKey: 'biology',
        category: 'membranes',
        name: 'Membrane Transport',
        description:
          'Explore the fluid mosaic model with real membrane proteins, then watch diffusion, osmosis and the sodium–potassium pump.',
        path: '/learn/study/subjects/biology/tools/membrane-transport',
        actionLabel: 'Open Membrane Transport',
        icon: Droplets,
        tone: 'sky',
      },
      {
        id: 'cell-energy',
        subjectKey: 'biology',
        category: 'energy',
        name: 'Cell Energy',
        description:
          'Follow carbon through photosynthesis and respiration, test limiting factors, and watch ATP synthase turn like a turbine.',
        path: '/learn/study/subjects/biology/tools/cell-energy',
        actionLabel: 'Open Cell Energy',
        icon: Sun,
        tone: 'sunshine',
      },
      {
        id: 'cell-division',
        subjectKey: 'biology',
        category: 'cell-division',
        name: 'Cell Division',
        description:
          'Watch chromosomes copy, line up and separate in 3D mitosis and meiosis, and zoom from DNA to a whole chromosome.',
        path: '/learn/study/subjects/biology/tools/cell-division',
        actionLabel: 'Open Cell Division',
        icon: Split,
        tone: 'coral',
      },
      {
        id: 'genetics',
        subjectKey: 'biology',
        category: 'inheritance',
        name: 'Genetics',
        description:
          'Predict offspring with Punnett squares, see why real families differ from 3 : 1, and work out how a trait runs in a family.',
        path: '/learn/study/subjects/biology/tools/genetics',
        actionLabel: 'Open Genetics',
        icon: GitFork,
        tone: 'sunshine',
      },
      {
        id: 'protein-factory',
        subjectKey: 'biology',
        category: 'dna-proteins',
        name: 'Protein Factory',
        description:
          'Watch RNA polymerase copy a gene into mRNA, then a ribosome read it three bases at a time and build a protein. Real molecule shapes.',
        path: '/learn/study/subjects/biology/tools/protein-factory',
        actionLabel: 'Open Protein Factory',
        icon: Dna,
        tone: 'sky',
      },
      {
        id: 'gene-expression',
        subjectKey: 'biology',
        category: 'dna-proteins',
        name: 'Gene Expression & Mutation',
        description:
          'Change a gene one base at a time and see the protein change, read a karyotype, and switch the trp operon on and off.',
        path: '/learn/study/subjects/biology/tools/gene-expression',
        actionLabel: 'Open Gene Expression & Mutation',
        icon: Dna,
        tone: 'coral',
      },
      {
        id: 'enzyme-lab',
        subjectKey: 'biology',
        category: 'enzymes',
        name: 'Enzyme Lab',
        description:
          'Watch real enzymes grip their substrate, then test temperature, pH, substrate and inhibitors in a virtual test tube.',
        path: '/learn/study/subjects/biology/tools/enzyme-lab',
        actionLabel: 'Open Enzyme Lab',
        icon: FlaskConical,
        tone: 'mint',
      },
      {
        id: 'immune-defence',
        subjectKey: 'biology',
        category: 'immunity',
        name: 'Immune Defence',
        description:
          'See real antibodies and viruses in 3D, follow an immune response step by step, and test vaccines and herd immunity.',
        path: '/learn/study/subjects/biology/tools/immune-defence',
        actionLabel: 'Open Immune Defence',
        icon: ShieldPlus,
        tone: 'coral',
      },
      {
        id: 'evolution',
        subjectKey: 'biology',
        category: 'evolution',
        name: 'Evolution',
        description:
          'Watch birds change a beetle population over generations, see genetic drift, and build an evolutionary tree.',
        path: '/learn/study/subjects/biology/tools/evolution',
        actionLabel: 'Open Evolution',
        icon: Bird,
        tone: 'sunshine',
      },
      {
        id: 'biotechnology-lab',
        subjectKey: 'biology',
        category: 'biotechnology',
        name: 'Biotechnology Lab',
        description:
          'Watch real Taq polymerase copy DNA in PCR, run a gel, cut and join DNA with restriction enzymes, and aim CRISPR–Cas9 at a gene.',
        path: '/learn/study/subjects/biology/tools/biotechnology-lab',
        actionLabel: 'Open Biotechnology Lab',
        icon: Scissors,
        tone: 'sky',
      },
      {
        id: 'reproduction',
        subjectKey: 'biology',
        category: 'reproduction',
        name: 'Reproduction',
        description:
          'Compare clones and seedlings when a disease strikes, look inside a sperm and an egg, follow fertilisation, and watch a bee pollinate a flower.',
        path: '/learn/study/subjects/biology/tools/reproduction',
        actionLabel: 'Open Reproduction',
        icon: Flower,
        tone: 'coral',
      },
      {
        id: 'virtual-microscope',
        subjectKey: 'biology',
        category: 'cells',
        name: 'Virtual Microscope',
        description:
          'Look at onion, cheek, pondweed and blood cells at 40× to 400×, learn the parts of a microscope, and measure cells.',
        path: '/learn/study/subjects/biology/tools/virtual-microscope',
        actionLabel: 'Open Virtual Microscope',
        icon: Microscope,
        tone: 'sky',
      },
      {
        id: 'growth-lab',
        subjectKey: 'biology',
        category: 'plant-growth',
        name: 'Growth & Survival Lab',
        description:
          'Grow plants in six pots and change one condition at a time, then watch woodlice choose damp or dry in a choice chamber.',
        path: '/learn/study/subjects/biology/tools/growth-lab',
        actionLabel: 'Open Growth & Survival Lab',
        icon: Sprout,
        tone: 'mint',
      },
      {
        id: 'ecosystems',
        subjectKey: 'biology',
        category: 'ecosystems',
        name: 'Ecosystems',
        description:
          'Remove a species from an Australian woodland food web, follow energy up the pyramid, and watch rabbit and fox numbers cycle.',
        path: '/learn/study/subjects/biology/tools/ecosystems',
        actionLabel: 'Open Ecosystems',
        icon: Trees,
        tone: 'mint',
      },
      {
        id: 'body-systems',
        subjectKey: 'biology',
        category: 'body-systems',
        name: 'Body Systems',
        description:
          'Explore the organs in a see-through body, follow oxygen and glucose, and see how the body controls glucose and temperature.',
        path: '/learn/study/subjects/biology/tools/body-systems',
        actionLabel: 'Open Body Systems',
        icon: HeartPulse,
        tone: 'coral',
      },
      {
        id: 'classification-keys',
        subjectKey: 'biology',
        category: 'classification',
        name: 'Classification & Keys',
        description:
          'Identify mystery invertebrates with a dichotomous key, write your own key, and compare animals from kingdom to species.',
        path: '/learn/study/subjects/biology/tools/classification-keys',
        actionLabel: 'Open Classification & Keys',
        icon: ListTree,
        tone: 'sunshine',
      },
      {
        id: 'plant-transport-lab',
        subjectKey: 'biology',
        category: 'plant-transport',
        name: 'Plant Transport Lab',
        description:
          'Measure transpiration with a potometer as you change light, temperature, humidity and wind, and follow water and sugar through xylem and phloem.',
        path: '/learn/study/subjects/biology/tools/plant-transport-lab',
        actionLabel: 'Open Plant Transport Lab',
        icon: Leaf,
        tone: 'mint',
      },
      {
        id: 'field-sampling-lab',
        subjectKey: 'biology',
        category: 'ecosystems',
        name: 'Field Sampling Lab',
        description:
          'Estimate a population with quadrats and see how sample size matters, walk a transect from shade to sun, and use mark and recapture.',
        path: '/learn/study/subjects/biology/tools/field-sampling-lab',
        actionLabel: 'Open Field Sampling Lab',
        icon: Grid3x3,
        tone: 'sky',
      },
    ]
  : [];
